/**
 * A controller for enabling/disabling elements based on a checkbox state.
 * Unlike dependent_checkbox_controller, this works with any element (links,
 * buttons) not just checkboxes.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="checkbox-enable"
 *      data-checkbox-enable-disabled-class="btn-disabled pointer-events-none">
 *   <label>
 *     <input type="checkbox" data-action="change->checkbox-enable#toggle">
 *     I confirm I am 18 or older
 *   </label>
 *
 *   <a href="/menu"
 *      data-checkbox-enable-target="dependent"
 *      class="btn btn-disabled pointer-events-none">
 *     View menu
 *   </a>
 * </div>
 * ```
 *
 * Targets:
 * - `dependent`: Elements to enable/disable based on checkbox state
 *
 * Classes:
 * - `disabled`: Space-separated classes to add when disabled (e.g.
 *   "btn-disabled pointer-events-none")
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dependent"];
  static classes = ["disabled"];

  connect() {
    // Ensure dependents start disabled
    this.disable();
  }

  toggle(event) {
    if (event.target.checked) {
      this.enable();
    } else {
      this.disable();
    }
  }

  disable() {
    this.dependentTargets.forEach((element) => {
      element.setAttribute("aria-disabled", "true");
      this.disabledClasses.forEach((cls) => {
        element.classList.add(cls);
      });
    });
  }

  enable() {
    this.dependentTargets.forEach((element) => {
      element.removeAttribute("aria-disabled");
      this.disabledClasses.forEach((cls) => {
        element.classList.remove(cls);
      });
    });
  }
}
